import mapboxgl, { LngLatBoundsLike, MapboxOptions } from 'mapbox-gl'
import { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { DefaultConfig } from './config'
import 'mapbox-gl/dist/mapbox-gl.css'
import './overwrites.css'
import { InteractiveToggle } from './InteractiveToggle'
import { useWindowSize } from 'usehooks-ts'
import { executeWhenReady } from './helpers'

export const MapBoxMap = ({
  config,
  dataSource = '',
  panAllowed = false,
  maxExtendBounds,
  viewBounds,
  animationDuration = 150,
  showInteractionToggle = true,
  interActionToggle,
  height,
  center,
  init,
  children,
  handleInteractivityChange,
}: {
  config?: Partial<MapboxOptions>
  panAllowed?: boolean
  dataSource?: string
  maxExtendBounds?: number[][]
  viewBounds?: number[][]
  center?: number[]
  animationDuration?: number
  showInteractionToggle?: boolean
  interActionToggle?: FC<any>
  height?: number
  init?: (map: mapboxgl.Map) => void
  children?: JSX.Element[] | JSX.Element | string
  handleInteractivityChange?: (e: boolean) => void
}) => {
  // const [map, setMap] = useState<mapboxgl.Map>()

  const map = useRef<mapboxgl.Map | undefined>(null)
  const mapContainer = useRef<HTMLDivElement>(null)
  const [isActive, setActive] = useState(false)
  const size = useWindowSize()
  const navControl = useRef<mapboxgl.NavigationControl>(
    new mapboxgl.NavigationControl()
  )
  useEffect(() => {
    if (handleInteractivityChange) handleInteractivityChange(isActive)
  }, [isActive])

  useEffect(() => {
    if (map.current) return
    map.current = new mapboxgl.Map({
      ...DefaultConfig,
      ...config,
      container: mapContainer.current,
    })
    map.current.addControl(
      new mapboxgl.AttributionControl({
        customAttribution: '<b>' + dataSource + '</b>',
      }),
      'bottom-right'
    )

    map.current.once('idle', () => fitBounds(viewBounds))
  })

  useEffect(() => {
    if (!map.current) return
    if (init) init(map.current)
  }, [map.current])

  const fitBounds = (bounds: number[][] | undefined) => {
    if (bounds && map.current) {
      if (config && config.pitch) map.current.setPitch(config.pitch)

      map.current.resize()

      executeWhenReady(map.current, () =>
        map.current.fitBounds(bounds as LngLatBoundsLike, {
          padding: 0,
          duration: animationDuration,
        })
      )
    }
  }

  useEffect(() => {
    if (!map.current) return
    setInteractive(false)
  }, [map])

  useEffect(() => {
    if (!map.current) return
    map.current.setMaxBounds(maxExtendBounds as LngLatBoundsLike)
  }, [maxExtendBounds, size, isActive])

  useEffect(() => {
    if (!map.current) return
    fitBounds(viewBounds)
  }, [viewBounds, size, isActive])
  useEffect(() => {
    if (!map || !center) return

    map.current.once('idle', () => {
      map.current.setCenter(center as [number, number])
    })
  }, [center, size, isActive])

  useEffect(() => {
    if (!map.current) return
    if (isActive) {
      setInteractive(true)
      map.current.getContainer().scrollIntoView({ behavior: 'smooth' })
    }
    if (!isActive) {
      setInteractive(false)
    }
  }, [maxExtendBounds, isActive])

  const setInteractive = (value: boolean) => {
    if (!map.current) return
    if (value === true) {
      // map.current.boxZoom.enable()
      map.current.scrollZoom.enable()
      map.current.keyboard.enable()
      map.current.doubleClickZoom.enable()
      map.current.touchZoomRotate.enable()
      map.current.dragPan.enable()

      if (panAllowed) {
        map.current.dragRotate.enable()
      }
      map.current.dragPan.enable()
      map.current.addControl(navControl.current, 'top-right')
    }
    if (!value) {
      map.current.boxZoom.disable()
      map.current.scrollZoom.disable()
      map.current.dragPan.disable()
      map.current.dragRotate.disable()
      map.current.keyboard.disable()
      map.current.doubleClickZoom.disable()
      map.current.touchZoomRotate.disable()
      map.current.removeControl(navControl.current)
    }
  }

  return (
    <Sizer isActive={isActive} height={height} className='size'>
      {showInteractionToggle && (
        <InteractiveToggle
          current={isActive}
          change={setActive}
          Visual={interActionToggle}
        />
      )}
      <_MapBoxMap isActive={isActive} ref={mapContainer} />
      {children}
    </Sizer>
  )
}

const Sizer = styled.div<{ isActive: boolean; height?: number }>`
  width: 100%;
  overflow: hidden;
  margin-bottom: 60px;
  padding-bottom: 90vh;
  position: relative;
  pointer-events: none;
  z-index: 0;
  position: relative;
  @media only screen and (max-width: 711px) {
    margin-top: 0;
    padding-bottom: ${({ isActive }) => (isActive ? '100vh' : '100vh')};
  }
`

const _MapBoxMap = styled.div.attrs({ id: 'map' })<{ isActive: boolean }>`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  max-height: 100vh;
  font-size: 10px;
  pointer-events: all;
  z-index: 0;
  @media only screen and (max-width: 711px) {
    max-height: 100vh;
  }

  .mapboxgl-ctrl-bottom-left {
    right: 10px;
    bottom: 2px;
    left: unset;
  }
  .mapboxgl-ctrl-attrib,
  .mapboxgl-ctrl-attrib-inner {
    * {
      color: var(--brandblue-bright-3) !important;
    }
    background-color: transparent !important;

    color: var(--brandblue-bright-3) !important;
  }
  .mapboxgl-ctrl-bottom-right {
    bottom: 10px;
    right: 110px;
    background-color: transparent !important;

    @media only screen and (max-width: 711px) {
      bottom: -3px;
      right: 100px;
    }
  }
`
